import { ProcessedSearchResultItem } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { useAddToList } from '@app/hooks/useAddToList';
import { trackGA4AddToWishListEvent } from '@app/pages/i/analytics';
import {
  ListsButtonInventoryItem,
  ListsInclusions,
} from '@app/types/shop-bff/types';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import { withErrorBoundary } from '@redbubble/next/error-boundary';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { AnalyticsItemListID } from '@redbubble/next-analytics/gtm';
import { SearchUUIDStorageKeyId } from 'utils/searchUUIDStorage';
import { FavoriteIcon } from './FavoriteIcon';

export const messages = defineMessages({
  addToList: {
    defaultMessage: 'Add to list',
  },
  removeFromList: {
    defaultMessage: 'Remove from list',
  },
});

export interface AddToListButtonProps {
  inventoryItem: ListsButtonInventoryItem | ProcessedSearchResultItem;
  workId: string;
  analyticsComponentID: AnalyticsItemListID;
  analyticsIndex: number;
  searchUUIDStorageKey?: SearchUUIDStorageKeyId;
  onAddToList?: () => void;
  listsInclusions: ListsInclusions | null;
  size?: string;
  elevation?: string;
  isMobile?: boolean;
}

export const AddToListButton: React.FC<AddToListButtonProps> =
  withErrorBoundary(
    ({
      inventoryItem,
      workId,
      analyticsComponentID,
      analyticsIndex,
      searchUUIDStorageKey,
      onAddToList,
      listsInclusions,
      size = constants.SMALL,
      elevation = constants.ELEVATION_MEDIUM,
      isMobile,
    }) => {
      const intl = useIntl();

      const handleAddToList = () => {
        trackGA4AddToWishListEvent({
          item: inventoryItem,
          workId,
          analyticsComponentID,
          itemIndex: analyticsIndex,
          searchUUIDStorageKey,
        });

        if (onAddToList) {
          onAddToList();
        }
      };

      const { itemIsFavorited, handleOnToggleList, ListModal } = useAddToList({
        inventoryItem,
        initialListsInclusions: listsInclusions,
        onAddToList: handleAddToList,
      });

      const ariaLabel = intl.formatMessage(
        itemIsFavorited ? messages.removeFromList : messages.addToList
      );

      const title = intl.formatMessage(
        itemIsFavorited ? messages.removeFromList : messages.addToList
      );

      return (
        <>
          <Button
            data-testid="add-to-list-button"
            intent={!isMobile && constants.PLAIN}
            elevation={isMobile && elevation}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              handleOnToggleList();
            }}
            circle={isMobile}
            size={size}
            iconAfter={
              isMobile && (
                <FavoriteIcon favorited={itemIsFavorited} size={size} />
              )
            }
            aria-label={ariaLabel}
            title={title}
          >
            {!isMobile && (
              <FavoriteIcon favorited={itemIsFavorited} size={size} />
            )}
          </Button>
          {ListModal}
        </>
      );
    }
  );
